import type {
  FeedbackCatalogFormDefinition,
  FeedbackTemplateLocale,
  ResolvedFeedbackFormCatalogItem,
} from './types';

/**
 * Broad hospitality / feedback form kits. Never auto-written to a workspace —
 * operators pick from the library, then own and edit the copy.
 */
export const FEEDBACK_FORM_CATALOG: FeedbackCatalogFormDefinition[] = [
  {
    key: 'visit-standard',
    category: 'visit',
    locales: {
      de: {
        name: 'Standard-Besuch',
        description:
          'Allgemeines Feedback nach dem Besuch — was gut lief und was besser werden kann.',
        title: 'Wie war Ihr Besuch?',
        questions: [
          {
            type: 'short_text',
            label: 'Was ist Ihnen besonders positiv aufgefallen?',
            required: false,
          },
          {
            type: 'short_text',
            label: 'Was könnten wir verbessern?',
            required: false,
          },
        ],
      },
      en: {
        name: 'Standard visit',
        description:
          'Everyday post-visit feedback — what went well and what to improve.',
        title: 'How was your visit?',
        questions: [
          {
            type: 'short_text',
            label: 'What went especially well?',
            required: false,
          },
          {
            type: 'short_text',
            label: 'What could we improve?',
            required: false,
          },
        ],
      },
    },
  },
  {
    key: 'stay-nps',
    category: 'pulse',
    locales: {
      de: {
        name: 'Kurzer Empfehlungs-Check',
        description:
          'Weiterempfehlung auf einer Skala von 0–10 plus ein Freitext — schnell ausfüllbar nach Übernachtung oder Besuch.',
        title: 'Eine kurze Frage zu Ihrem Aufenthalt',
        questions: [
          {
            type: 'nps_0_10',
            label: 'Wie wahrscheinlich ist es, dass Sie uns weiterempfehlen?',
            required: true,
          },
          {
            type: 'long_text',
            label: 'Was ist der wichtigste Grund für Ihre Bewertung?',
            required: false,
          },
        ],
      },
      en: {
        name: 'Quick recommendation check',
        description:
          'Recommendation score from 0–10 plus one open note — fast after a stay or visit.',
        title: 'A quick question about your stay',
        questions: [
          {
            type: 'nps_0_10',
            label: 'How likely are you to recommend us?',
            required: true,
          },
          {
            type: 'long_text',
            label: 'What is the main reason for your score?',
            required: false,
          },
        ],
      },
    },
  },
  {
    key: 'service-quality',
    category: 'service',
    locales: {
      de: {
        name: 'Service & Atmosphäre',
        description:
          'Service, Ambiente und Weiterempfehlung — für Restaurant, Café und Hotel-Alltag.',
        title: 'Wie war der Service bei uns?',
        questions: [
          {
            type: 'rating_1_5',
            label: 'Wie bewerten Sie den Service?',
            required: true,
          },
          {
            type: 'rating_1_5',
            label: 'Wie war die Atmosphäre?',
            required: false,
          },
          {
            type: 'yes_no',
            label: 'Würden Sie uns weiterempfehlen?',
            required: false,
          },
          {
            type: 'long_text',
            label: 'Möchten Sie uns noch etwas mitteilen?',
            required: false,
          },
        ],
      },
      en: {
        name: 'Service & atmosphere',
        description:
          'Service, ambience, and recommendation — for restaurants, cafés, and hotels.',
        title: 'How was the service with us?',
        questions: [
          {
            type: 'rating_1_5',
            label: 'How would you rate the service?',
            required: true,
          },
          {
            type: 'rating_1_5',
            label: 'How was the atmosphere?',
            required: false,
          },
          {
            type: 'yes_no',
            label: 'Would you recommend us?',
            required: false,
          },
          {
            type: 'long_text',
            label: 'Anything else we should know?',
            required: false,
          },
        ],
      },
    },
  },
  {
    key: 'event-catering',
    category: 'event',
    locales: {
      de: {
        name: 'Event / Catering',
        description:
          'Nach Event oder Catering — Service, Essen & Trinken, Weiterempfehlung.',
        title: 'Wie war das Event mit uns?',
        questions: [
          {
            type: 'rating_1_5',
            label: 'Wie war der Service?',
            required: true,
          },
          {
            type: 'rating_1_5',
            label: 'Wie waren Essen & Getränke?',
            required: false,
          },
          {
            type: 'yes_no',
            label: 'Würden Sie uns weiterempfehlen?',
            required: false,
          },
          {
            type: 'long_text',
            label: 'Gibt es etwas, das wir wissen sollten?',
            required: false,
          },
        ],
      },
      en: {
        name: 'Event / catering',
        description:
          'After a job or event — service, food & drink, and recommendation.',
        title: 'How was the event with us?',
        questions: [
          {
            type: 'rating_1_5',
            label: 'How was the service?',
            required: true,
          },
          {
            type: 'rating_1_5',
            label: 'How was the food & drink?',
            required: false,
          },
          {
            type: 'yes_no',
            label: 'Would you recommend us?',
            required: false,
          },
          {
            type: 'long_text',
            label: 'Anything else we should know?',
            required: false,
          },
        ],
      },
    },
  },
  {
    key: 'team-hospitality',
    category: 'service',
    locales: {
      de: {
        name: 'Team & Gastfreundschaft',
        description:
          'Fokus auf Personal, Freundlichkeit und Wartezeit — klar und gastorientiert.',
        title: 'Wie hat unser Team Sie betreut?',
        questions: [
          {
            type: 'rating_1_5',
            label: 'Wie freundlich war unser Team?',
            required: true,
          },
          {
            type: 'rating_1_5',
            label: 'Wie zufrieden waren Sie mit der Wartezeit?',
            required: false,
          },
          {
            type: 'short_text',
            label: 'Gab es eine Person, die besonders hilfreich war?',
            required: false,
          },
          {
            type: 'long_text',
            label: 'Was können wir für den nächsten Besuch verbessern?',
            required: false,
          },
        ],
      },
      en: {
        name: 'Team & hospitality',
        description:
          'Staff friendliness and wait time — guest-facing and operational.',
        title: 'How did our team look after you?',
        questions: [
          {
            type: 'rating_1_5',
            label: 'How friendly was our team?',
            required: true,
          },
          {
            type: 'rating_1_5',
            label: 'How satisfied were you with the wait time?',
            required: false,
          },
          {
            type: 'short_text',
            label: 'Was anyone especially helpful?',
            required: false,
          },
          {
            type: 'long_text',
            label: 'What should we improve for next time?',
            required: false,
          },
        ],
      },
    },
  },
  {
    key: 'quick-check',
    category: 'pulse',
    locales: {
      de: {
        name: 'Schnell-Check',
        description:
          'Minimal — eine Verbesserungsfrage und Ja/Nein zur Weiterempfehlung.',
        title: 'Kurzes Feedback, bitte',
        questions: [
          {
            type: 'yes_no',
            label: 'Hat alles Ihren Erwartungen entsprochen?',
            required: true,
          },
          {
            type: 'short_text',
            label: 'Was sollen wir als Nächstes besser machen?',
            required: false,
          },
        ],
      },
      en: {
        name: 'Quick check',
        description:
          'Minimal — expectations check plus one improvement prompt.',
        title: 'A quick note, please',
        questions: [
          {
            type: 'yes_no',
            label: 'Did everything meet your expectations?',
            required: true,
          },
          {
            type: 'short_text',
            label: 'What should we improve next?',
            required: false,
          },
        ],
      },
    },
  },
];

export function listFeedbackFormCatalog(
  locale: FeedbackTemplateLocale,
): ResolvedFeedbackFormCatalogItem[] {
  return FEEDBACK_FORM_CATALOG.map((item) =>
    resolveFeedbackFormCatalogItem(item, locale),
  );
}

export function getFeedbackFormCatalogItem(
  key: string,
  locale: FeedbackTemplateLocale,
): ResolvedFeedbackFormCatalogItem | null {
  const item = FEEDBACK_FORM_CATALOG.find((entry) => entry.key === key);
  if (!item) return null;
  return resolveFeedbackFormCatalogItem(item, locale);
}

function resolveFeedbackFormCatalogItem(
  item: FeedbackCatalogFormDefinition,
  preferred: FeedbackTemplateLocale,
): ResolvedFeedbackFormCatalogItem {
  const locale = item.locales[preferred] ? preferred : 'de';
  const localized = item.locales[locale] ?? item.locales.de;
  return {
    key: item.key,
    category: item.category,
    locale,
    name: localized.name,
    description: localized.description,
    title: localized.title,
    questions: localized.questions,
    availableLocales: Object.keys(item.locales) as FeedbackTemplateLocale[],
  };
}
